import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
} from '@/components/ui/combobox';

export type InlineSelectOption = { label: string; value: string };

export type InlineSelectGroup = {
  label: string;
  items: InlineSelectOption[];
};

type InlineSelectCellProps = {
  /** Flat list, or groups when the options come from more than one source. */
  options?: InlineSelectOption[];
  groups?: InlineSelectGroup[];
  value: string | null;
  onChange: (next: string | null) => void | Promise<unknown>;
  /** Rendered inside the trigger; falls back to the matched option's label. */
  children?: React.ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Offers a "clear" entry at the top of the list. */
  clearLabel?: string;
  disabled?: boolean;
  ariaLabel: string;
  triggerClassName?: string;
  contentClassName?: string;
  field?: string;
};

const CLEAR_VALUE = '__inline_select_clear__';

/**
 * A table cell that edits in place: the current value reads as text until it is
 * clicked, then becomes a searchable single-select anchored to the cell.
 *
 * Built on the Base UI combobox rather than a plain select because the assignee
 * list runs to a whole incident roster, where typing to filter is the only
 * usable interaction. The trigger stays a button so keyboard users reach it in
 * tab order and the Phase 4 single-key row actions have something to open.
 */
export const InlineSelectCell: React.FC<InlineSelectCellProps> = ({
  options,
  groups,
  value,
  onChange,
  children,
  placeholder = 'Set value',
  searchPlaceholder = 'Search…',
  clearLabel,
  disabled = false,
  ariaLabel,
  triggerClassName,
  contentClassName,
  field,
}) => {
  const anchor = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const flatOptions = useMemo(
    () => groups?.flatMap((group) => group.items) ?? options ?? [],
    [groups, options]
  );

  /**
   * Items are the option objects rather than bare ids so the popup can search
   * on the label while `onValueChange` still hands back something
   * identifiable.
   */
  const clearOption = useMemo(
    () => (clearLabel ? { label: clearLabel, value: CLEAR_VALUE } : null),
    [clearLabel]
  );

  const items = useMemo(() => {
    if (groups) {
      return [
        ...(clearOption ? [{ label: '', items: [clearOption] }] : []),
        ...groups,
      ];
    }
    return clearOption ? [clearOption, ...flatOptions] : flatOptions;
  }, [groups, clearOption, flatOptions]);

  const selected = useMemo(
    () => flatOptions.find((option) => option.value === value) ?? null,
    [flatOptions, value]
  );

  const handleChange = (next: InlineSelectOption | null) => {
    setOpen(false);
    const nextValue = !next || next.value === CLEAR_VALUE ? null : next.value;
    if (nextValue === value) return;
    void onChange(nextValue);
  };

  const label = children ?? selected?.label;

  return (
    <Combobox
      items={items}
      value={selected}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      itemToStringValue={(item) => (item as InlineSelectOption).label}
      onValueChange={(next) => handleChange(next as InlineSelectOption | null)}
    >
      <div ref={anchor}>
        <ComboboxTrigger
          aria-label={ariaLabel}
          data-action-field={field}
          className={classNames(
            'inline-edit-cell',
            { 'inline-edit-cell--empty': !label },
            triggerClassName
          )}
        >
          <span className="text-truncate">{label ?? placeholder}</span>
        </ComboboxTrigger>
      </div>
      <ComboboxContent
        anchor={anchor}
        className={classNames('dark', contentClassName)}
      >
        <ComboboxInput placeholder={searchPlaceholder} showTrigger={false} />
        <ComboboxEmpty>No matches</ComboboxEmpty>
        <ComboboxList>
          {groups
            ? (group: InlineSelectGroup, index: number) => (
                <React.Fragment key={group.label || `group-${index}`}>
                  {index > 0 && <ComboboxSeparator />}
                  <ComboboxGroup items={group.items}>
                    {group.label ? (
                      <ComboboxLabel>{group.label}</ComboboxLabel>
                    ) : null}
                    <ComboboxCollection>
                      {(item: InlineSelectOption) => (
                        <ComboboxItem key={item.value} value={item}>
                          {item.label}
                        </ComboboxItem>
                      )}
                    </ComboboxCollection>
                  </ComboboxGroup>
                </React.Fragment>
              )
            : (item: InlineSelectOption) => (
                <ComboboxItem key={item.value} value={item}>
                  {item.label}
                </ComboboxItem>
              )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default InlineSelectCell;
